import type {MetaDescriptor} from 'react-router'
import type {LivePackDefinition} from './types'
import {mirroraiOrganization} from '@core/products/organization'
import {packOgImageUrl} from '@core/og/cardUrls'
import {NARCIS_PACK_SAMPLES_BASE_URL} from '@product/narcis/config'
import {HOW_IT_WORKS, MODEL_PRICE, OG_LINE, PACK_FAQ, PRICE_LINE} from '@product/narcis/proposition'
import {
    buildBreadcrumbList,
    buildDefinedTerm,
    buildFAQPage,
    buildHowTo,
    buildImageObject,
    buildProduct,
    buildWebApplication,
    digitalFreeShipping,
    digitalReturnPolicy14d,
    jsonLd,
    type JsonLd,
} from '@core/lib/jsonld'

const NARCIS_BASE_URL = 'https://narcis.ai'

/** The pack's card, an asset written when the pack ships; it states the object and the price (canon § 7). */
function ogImageUrlFor(slug: string): string {
    return packOgImageUrl('narcis', NARCIS_PACK_SAMPLES_BASE_URL, slug)
}

/** `og:description` states the price line of the canon; `description` is the pack's own, the price line after it
 * (the snippet is where a price-intent search reads the price; the pack files carry none). */
export function buildPackMeta(pack: LivePackDefinition): MetaDescriptor[] {
    const ogImageUrl = ogImageUrlFor(pack.slug)
    const ogDescription = OG_LINE
    return [
        {title: pack.seoTitle ?? pack.title},
        {name: 'description', content: `${pack.metaDescription.trim()} ${PRICE_LINE}`},
        {property: 'og:title', content: pack.ogTitle ?? pack.title},
        {property: 'og:description', content: ogDescription},
        {property: 'og:url', content: `${NARCIS_BASE_URL}/${pack.slug}`},
        {property: 'og:image', content: ogImageUrl},
        {property: 'og:image:secure_url', content: ogImageUrl},
        {property: 'og:image:width', content: '1200'},
        {property: 'og:image:height', content: '630'},
        {property: 'og:image:type', content: 'image/png'},
        {property: 'twitter:card', content: 'summary_large_image'},
        {property: 'twitter:url', content: `${NARCIS_BASE_URL}/${pack.slug}`},
        {property: 'twitter:title', content: pack.ogTitle ?? pack.title},
        {property: 'twitter:description', content: ogDescription},
        {property: 'twitter:image', content: ogImageUrl},
        {tagName: 'link', rel: 'canonical', href: `${NARCIS_BASE_URL}/${pack.slug}`},
    ]
}

/** The pack's own questions, then the canon's: the visible FAQ and the FAQPage JSON-LD both read this. */
export const packFaq = (pack: LivePackDefinition) => [...(pack.seo.faq ?? []), ...PACK_FAQ]

export function buildPackJsonLd(pack: LivePackDefinition): JsonLd[] {
    const packUrl = `${NARCIS_BASE_URL}/${pack.slug}`
    const ogImageUrl = ogImageUrlFor(pack.slug)
    // The pack is reached through the one sale, the model.
    const price = (MODEL_PRICE.amountCents / 100).toFixed(2)
    const schemas: JsonLd[] = []

    schemas.push(
        buildBreadcrumbList([
            {name: 'Home', url: `${NARCIS_BASE_URL}/`},
            {name: pack.hero.headline, url: packUrl},
        ]),
    )

    schemas.push(
        buildWebApplication({
            name: pack.title,
            description: pack.metaDescription,
            url: packUrl,
            applicationCategory: 'MultimediaApplication',
            offers: {priceCurrency: MODEL_PRICE.currency},
            screenshot: ogImageUrl,
        }),
    )

    schemas.push(
        buildHowTo({
            name: `How to get ${pack.hero.headline}`,
            description: pack.metaDescription,
            steps: HOW_IT_WORKS.map((step) => ({name: step.title, text: step.body})),
            creator: mirroraiOrganization,
        }),
    )

    if (pack.seo.prepTips && pack.seo.prepTips.steps.length > 0) {
        schemas.push(
            buildHowTo({
                name: pack.seo.prepTips.title,
                description: pack.seo.prepTips.intro ?? '',
                steps: pack.seo.prepTips.steps,
                creator: mirroraiOrganization,
            }),
        )
    }

    schemas.push(buildFAQPage(packFaq(pack)))

    if (pack.seo.useCases && pack.seo.useCases.items.length > 0) {
        schemas.push(
            jsonLd('ItemList', {
                name: pack.seo.useCases.title,
                itemListElement: pack.seo.useCases.items.map((text, i) => ({
                    '@type': 'ListItem',
                    position: i + 1,
                    name: text,
                })),
            }),
        )
    }

    // Product.image → real portrait (Task 3 — visual discovery plan 2026-06-01).
    // Using cover_portrait instead of the OG card URL so the Product rich result shows a
    // photo rather than the text/montage card Google would otherwise pick as representative.
    schemas.push(
        buildProduct({
            name: pack.hero.headline,
            description: pack.metaDescription,
            url: packUrl,
            image: pack.cover_portrait,
            brand: mirroraiOrganization,
            offer: {
                priceCurrency: MODEL_PRICE.currency,
                price,
                availability: 'https://schema.org/InStock',
                seller: mirroraiOrganization,
                shippingDetails: digitalFreeShipping(MODEL_PRICE.currency),
                hasMerchantReturnPolicy: digitalReturnPolicy14d(),
            },
        }),
    )

    // ImageObject metadata for curated sample portraits (Task 2 — visual discovery plan 2026-06-01).
    // Emits one ImageObject per curated sample, reusing the register-specific alt already written
    // for each image. cover_portrait gets representativeOfPage: true (the single strongest portrait).
    // Gate: samples.images.length > 0 (placeholder packs with no real samples are skipped).
    // No license/acquireLicensePage — badge dropped. No creator/copyrightNotice — legally awkward
    // for portraits of users' own faces (see jsonld.ts ImageObject comment).
    if (pack.samples.images.length > 0) {
        const coverSrc = pack.cover_portrait
        const seenCover = {emitted: false}
        for (const img of pack.samples.images) {
            const isHero = img.src === coverSrc && !seenCover.emitted
            if (isHero) seenCover.emitted = true
            schemas.push(
                buildImageObject({
                    contentUrl: img.src,
                    name: img.alt,
                    caption: img.alt,
                    ...(isHero && {representativeOfPage: true}),
                }),
            )
        }
        // If cover_portrait is not in samples.images (rare), emit it separately as the hero.
        if (!seenCover.emitted) {
            schemas.push(
                buildImageObject({
                    contentUrl: coverSrc,
                    name: pack.hero.headline,
                    caption: pack.hero.headline,
                    representativeOfPage: true,
                }),
            )
        }
    }

    if (pack.seo.definedTerm) {
        schemas.push(
            buildDefinedTerm({
                name: pack.seo.definedTerm.name,
                description: pack.seo.definedTerm.description,
                setName: 'Narcis.ai AI Tools Glossary',
            }),
        )
    }

    return schemas
}
